import React from 'react';

function AnimatedWavesBackground() {
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let width = window.innerWidth;
    let height = window.innerHeight;
    let time = 0;

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    }
    resize();
    window.addEventListener('resize', resize);

    function draw() {
      ctx.clearRect(0, 0, width, height);
      const colors = [
        'rgba(167,139,250,0.15)', 
        'rgba(147,112,219,0.12)', 
        'rgba(106,27,154,0.08)',
        'rgba(138,43,226,0.06)', // Added 4th wave
      ];
      for (let j = 0; j < 4; j++) { // Changed from 3 to 4 waves
        ctx.beginPath();
        for (let i = 0; i <= width; i += 3) {
          const amplitude = 20 + j * 12;
          const frequency = 0.002 + j * 0.001;
          const y =
            Math.sin(i * frequency + time * (0.010 + j * 0.002)) * amplitude + // Increased speed by ~20%
            Math.sin(i * frequency * 0.8 + time * (0.014 + j * 0.003)) * (amplitude * 0.6) +
            height * (0.7 + j * 0.08);
          if (i === 0) ctx.moveTo(i, y);
          else ctx.lineTo(i, y);
        }
        ctx.lineTo(width, height);
        ctx.lineTo(0, height);
        ctx.closePath();
        ctx.fillStyle = colors[j];
        ctx.fill();
      }
      time += 0.6; // Increased from 0.5 for ~20% speed boost
      animationFrameId = requestAnimationFrame(draw);
    }
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        zIndex: 1, // Above the main background but below content
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        opacity: 0.8,
      }}
      aria-hidden="true"
    />
  );
}

function AnimatedSmokeBackground() {
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let width = window.innerWidth;
    let height = window.innerHeight;
    let time = 0;

    // Initialize particles array first
    const particles = [];
    const PARTICLE_COUNT = 15;
    
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * width,
        y: height * 0.1 + Math.random() * height * 0.8,
        r: 80 + Math.random() * 120,
        baseX: Math.random() * width,
        baseY: height * 0.1 + Math.random() * height * 0.8,
        speed: 0.6 + Math.random() * 0.8,
        phase: Math.random() * Math.PI * 2,
        alpha: 0.2 + Math.random() * 0.3,
        colorIndex: i % 3,
      });
    }

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      
      // Now particles array exists, so we can redistribute them
      particles.forEach(particle => {
        particle.baseX = Math.random() * width;
        particle.baseY = height * 0.1 + Math.random() * height * 0.8;
      });
    }
    resize();
    window.addEventListener('resize', resize);

    const colors = [
      'rgba(167,139,250,',
      'rgba(147,112,219,', 
      'rgba(138,43,226,',
    ];

    function animate() {
      ctx.clearRect(0, 0, width, height);
      time += 0.04; // Reduced from 0.05 for ~20% slower

      particles.forEach((particle, index) => {
        const waveX = Math.sin(time * particle.speed + particle.phase) * 64; // Reduced from 80 for ~20% less movement
        const waveY = Math.cos(time * particle.speed * 0.8 + particle.phase) * 32; // Reduced from 40
        
        particle.x = particle.baseX + waveX + Math.sin(time * 0.48 + index) * 40; // Reduced from 0.6
        particle.y = particle.baseY + waveY + Math.cos(time * 0.32 + index) * 20; // Reduced from 0.4
        
        const pulseR = particle.r + Math.sin(time * 1.6 + index) * 16; // Reduced from 2.0 and 20
        const breathingAlpha = particle.alpha + Math.sin(time * 1.2 + index) * 0.08; // Reduced from 1.5

        const gradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, pulseR
        );
        gradient.addColorStop(0, colors[particle.colorIndex] + breathingAlpha + ')');
        gradient.addColorStop(0.6, colors[particle.colorIndex] + (breathingAlpha * 0.4) + ')');
        gradient.addColorStop(1, colors[particle.colorIndex] + '0)');
        
        ctx.save();
        ctx.globalAlpha = breathingAlpha;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, pulseR, 0, 2 * Math.PI);
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.restore();

        // Better boundary management to ensure full screen coverage
        if (particle.x < -200) {
          particle.baseX = width + 100;
        }
        if (particle.x > width + 200) {
          particle.baseX = -100;
        }
        if (particle.y < -200) {
          particle.baseY = height * 0.1 + Math.random() * height * 0.7;
        }
        if (particle.y > height + 200) {
          particle.baseY = height * 0.1 + Math.random() * height * 0.7;
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        zIndex: 2,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        opacity: 0.8, // Back to higher visibility
      }}
      aria-hidden="true"
    />
  );
}

const AnimatedBackground = () => {
  return (
    <>
      <AnimatedWavesBackground />
      <AnimatedSmokeBackground />
    </>
  );
};

export default AnimatedBackground;
